import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@radix-ui/react-collapsible'
import {type OmitStrict} from 'type-zoo'
import {grid, gridItem} from '~/design-system/foundations'
import {HgTextOutput, HgButtonGroup} from '~/design-system/hg/components'
import HgChevronIndicator from '~/design-system/hg/components/HgIcon/HgChevronIndicator'
import {externalLinks} from '~/routing/external-links'
import ButterflyChart from './_components/ButterflyChart'
import ComparisonTable from './_components/ComparisonTable'
import {type BankFeeCalculatorStatelessProps} from './types'
import {
  LEGACY_BANK_CHART_PROPS,
  MERCURY_CHART_PROPS,
  BUTTERFLY_CHART_Y_AXIS_KEY,
} from './constants'
import {
  type BankFeeConstantProps,
  type UsageBasedFee,
  type BankFeeProps,
} from './types'
import {
  getTransactionOverageCost,
  getTotalUSDWiresCost,
  getPercentageFeeCost,
  getTotalMonthlyFees,
  abbreviateAmount,
} from './utils'
import FormStateless from './_components/InputForm'
import MoneyRain from './_components/MoneyRain'
import {copyToClipboard} from '~/design-system/utils/copyToClipboard'
import {Analytics} from '~/utils/Analytics'

const BankFeeCalculatorStateless = ({
  formState,
  onFormChange,
  isFormDisabled,
  mercuryFeeConstants,
  legacyBankFeeConstants,
  annualSavingsCaption,
  setIsCopied,
  isCopied,
}: BankFeeCalculatorStatelessProps) => {
  const generateUSDWireFees = (wireFee: number, totalWires: number) => {
    return {
      fee: wireFee,
      transactionType: 'wire',
      numberOfTransactions: totalWires,
    }
  }

  const generateBankFeeProps = (
    feeConstants: OmitStrict<BankFeeConstantProps, 'transactionFee'> & {
      transactionFee: UsageBasedFee
    }
  ): BankFeeProps => {
    return {
      maintenanceFee: feeConstants.maintenanceFee,
      transactionFee: feeConstants.transactionFee,
      domesticUSDWireFee: {
        outgoing: generateUSDWireFees(
          feeConstants.domesticOutgoingUSDWireFee,
          formState.outgoingDomesticWires ?? 0
        ),
        incoming: generateUSDWireFees(
          feeConstants.domesticIncomingUSDWireFee,
          formState.incomingDomesticWires ?? 0
        ),
      },

      internationalUSDWireFee: {
        outgoing: generateUSDWireFees(
          feeConstants.internationalOutgoingUSDWireFee,
          formState.outgoingInternationalWires ?? 0
        ),
        incoming: generateUSDWireFees(
          feeConstants.internationalIncomingUSDWireFee,
          formState.incomingInternationalWires ?? 0
        ),
      },
      foreignCurrencyInternationalWireFee: {
        ...feeConstants.foreignCurrencyInternationalWiresFee,
        amount: formState.foreignCurrencyInternationalWires ?? 0,
      },
    }
  }

  const mercuryBankFeeProps = generateBankFeeProps({
    ...mercuryFeeConstants,
    transactionFee: {
      limit: mercuryFeeConstants.transactionFee.limit,
      feeOverLimit: {
        fee: mercuryFeeConstants.transactionFee.feeOverLimit.fee,
        transactionType: 'ACH',
        numberOfTransactions: formState.achTransactions ?? 0,
      },
    },
  })

  const legacyBankFeeProps = generateBankFeeProps({
    ...legacyBankFeeConstants,
    transactionFee: {
      limit: legacyBankFeeConstants.transactionFee.limit,
      feeOverLimit: {
        fee: legacyBankFeeConstants.transactionFee.feeOverLimit.fee,
        transactionType: 'transaction',
        numberOfTransactions: formState.totalTransactions ?? 0,
      },
    },
  })

  const generateChartData = () => {
    return [
      {
        feeType: 'Maintenance fees',
        mercury: mercuryBankFeeProps.maintenanceFee,
        legacyBank: legacyBankFeeProps.maintenanceFee,
      },
      {
        feeType: 'Trx. overage fees',
        mercury: getTransactionOverageCost(mercuryBankFeeProps.transactionFee),
        legacyBank: getTransactionOverageCost(legacyBankFeeProps.transactionFee),
      },
      {
        feeType: 'Wire (USD) fees',
        mercury: getTotalUSDWiresCost(
          mercuryBankFeeProps.domesticUSDWireFee,
          mercuryBankFeeProps.internationalUSDWireFee
        ),
        legacyBank: getTotalUSDWiresCost(
          legacyBankFeeProps.domesticUSDWireFee,
          legacyBankFeeProps.internationalUSDWireFee
        ),
      },
      {
        feeType: 'Wire (non-USD) fees',
        mercury: getPercentageFeeCost(
          mercuryBankFeeProps.foreignCurrencyInternationalWireFee
        ),
        legacyBank: getPercentageFeeCost(
          legacyBankFeeProps.foreignCurrencyInternationalWireFee
        ),
      },
    ]
  }

  const legacyBankTotalMonthlyFees = getTotalMonthlyFees(legacyBankFeeProps)
  const mercuryBankTotalMonthlyFees = getTotalMonthlyFees(mercuryBankFeeProps)

  const calculateAnnualSavings = () => {
    return legacyBankTotalMonthlyFees > mercuryBankTotalMonthlyFees
      ? (legacyBankTotalMonthlyFees - mercuryBankTotalMonthlyFees) * 12
      : 0
  }

  const annualSavings = calculateAnnualSavings()
  const showMoneyRain = annualSavings >= 1_000_000_000_000_000

  return (
    <div className="relative w-full">
      <div className={grid({class: 'mx-auto pt-s9'})}>
        <MoneyRain active={showMoneyRain} />
        <div
          className={gridItem({
            size: 'max',
            class:
              'flex flex-col rounded-xl bg-surface-elevated shadow-[0px_10px_16px_0px_rgba(28,28,35,0.02),0px_6px_10px_0px_rgba(28,28,35,0.04),0px_0px_3px_0px_rgba(28,28,35,0.09)]',
          })}
        >
          <div className="flex flex-col md:flex-row">
            <div className="flex-1 border-b border-solid border-border-frosted md:border-b-0 md:border-r">
              <FormStateless
                formState={formState}
                onChange={onFormChange}
                totalTransactionsLimit={mercuryBankFeeProps.transactionFee.limit}
                mercuryACHTransactionFee={
                  mercuryBankFeeProps.transactionFee.feeOverLimit.fee
                }
                isFormDisabled={isFormDisabled}
              />
            </div>
            <div className="flex-1">
              <HgTextOutput
                label={annualSavingsCaption}
                text={abbreviateAmount(calculateAnnualSavings(), {
                  threshold: 1_000_000,
                })}
              />
              <div>
                <ButterflyChart
                  leftChartProps={{
                    ...LEGACY_BANK_CHART_PROPS,
                    legendItemSubLabel: `${abbreviateAmount(legacyBankTotalMonthlyFees, {threshold: 10_000})}/mo.`,
                  }}
                  rightChartProps={{
                    ...MERCURY_CHART_PROPS,
                    legendItemSubLabel: `${abbreviateAmount(mercuryBankTotalMonthlyFees, {threshold: 10_000})}/mo.`,
                  }}
                  yAxisKey={BUTTERFLY_CHART_Y_AXIS_KEY}
                  data={generateChartData()}
                />
                <HgButtonGroup
                  className="flex flex-col gap-s2 p-s5 md:flex-row"
                  primaryButtonProps={{
                    children: 'Open Account',
                    href: externalLinks.auth.signup,
                    analyticsEventName: 'signup',
                    className: 'w-full',
                  }}
                  secondaryButtonProps={{
                    children: isCopied ? 'Copied' : 'Share Results',
                    className: 'w-full',
                    onClick: async () => {
                      // constructing search params separately since there is inconsistent behavior with encoding the curly brace in Chrome vs other browsers
                      const searchParams = new URLSearchParams(
                        window.location.search
                      )
                      const href = new URL(window.location.href)

                      const success = await copyToClipboard(
                        `${href.origin}${href.pathname}?${searchParams.toString()}`
                      )
                      Analytics.trackClick({
                        type: 'bank-fee-calculator/share-results',
                        customProperties: {
                          ...Object.fromEntries(
                            Object.entries(formState).map(([key, value]) => [
                              key,
                              value ?? undefined,
                            ])
                          ),
                        },
                      })

                      if (success) {
                        setIsCopied(true)
                      }
                    },
                  }}
                />
              </div>
            </div>
          </div>
          <Collapsible>
            <CollapsibleTrigger className="group w-full">
              <div className="flex h-[46px] min-h-[46px] w-full flex-row items-center justify-center border-t border-solid border-border-frosted px-s5 md:h-72 md:min-h-72">
                <p className="text-text-default arcadia-heading-9">
                  Compare monthly fees
                </p>
                <div className="flex h-40 min-h-40 w-40 min-w-40 items-center justify-center">
                  <HgChevronIndicator />
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden motion-safe:data-[state=closed]:animate-collapseUp motion-safe:data-[state=open]:animate-collapseDown motion-safe:data-[state=closed]:[animation-duration:_600ms] motion-safe:data-[state=open]:[animation-duration:_600ms]">
              <ComparisonTable
                legacyBankFees={legacyBankFeeProps}
                mercuryBankFees={mercuryBankFeeProps}
                legacyBankTotalMonthlyFees={legacyBankTotalMonthlyFees}
                mercuryBankTotalMonthlyFees={mercuryBankTotalMonthlyFees}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </div>
  )
}

export default BankFeeCalculatorStateless
